import { Action } from './FMTreeActions';
import * as constants from './FMTreeConstants';
import { combineReducers } from 'redux';
import produce from 'immer';

export const MIN_DISTANCE_BETWEEN_POINTS_DEFAULT_VALUE = 35;
export const MIN_DISTANCE_BETWEEN_POINTS_MIN_VALUE = 10;
export const ALLOWED_DEVIATION_FROM_LINE_DEFAULT_VALUE = 15;
export const ALLOWED_DEVIATION_FROM_LINE_MIN_VALUE = 5;

export enum GeofenceType {
    point = 'geopoint',
    line = 'geoline',
    polygon = 'geopolygon',
}

export enum ParamType {
    text = 'text',
    color = 'color',
    check = 'check',
    icon = 'icon',
    number = 'number',
    parentGroup = 'parent_group',
}

export enum ParamSysName {
    type = 'type',
    name = 'name',
    parentGroup = 'parent_group',
    color = 'color',
    radius = 'radius',
    geoLength = 'geoLength',
    forAddress = 'forAddress',
    showOnMap = 'showOnMap',
    description = 'description',
    icon = 'icon',
    culture = 'culture',
}

export interface FMTreeGroupData {
    id: string;
    name: string;
    parentId: string;
    childGroups: string[];
    childItems: string[];
}

export enum TreeNodeType {
    item = 'item',
    group = 'group',
}

export type FMTreeItemDataType = FMTreeItemData | FMTreeGeofenceItemData;

export interface FMTreeItemData {
    id: string;
    name: string;
    dataForTree: DataForTree;
}

interface DataForTree {
    parentId: string;
}

export interface FMTreeGeofenceItemData extends FMTreeItemData {
    type: GeofenceType;
}

export interface SelectedTreeNode {
    type: TreeNodeType;
    id: string;
}

export interface FMTreeDataItem {
    byIds: Record<string, FMTreeItemDataType | FMTreeGroupData>;
    allIds: string[];
}
export interface FMTreeDisplayItem {
    checked: string[];
    expanded?: string[];
}

export interface FMTreeData {
    isFetching: boolean;
    [TreeNodeType.item]: FMTreeDataItem;
    [TreeNodeType.group]: FMTreeDataItem;
}

export interface FMSharedDisplayState {
    [TreeNodeType.item]: FMTreeDisplayItem;
    [TreeNodeType.group]: FMTreeDisplayItem;
    selectedNode: SelectedTreeNode;
    infoPanelInEditMode: boolean;
    searchData: FMTreeSearchData;
    infoPanelFetching: boolean;
    nodeInfoData: NodeInfoData;
    nodeEditData: NodeEditData;
    viewType: TreeViewType;
    infoPanelInOptimizeMode: boolean;
    optimizeData: OptimizeData;
}

export enum TreeType {
    objectsTree = 'objectsTree',
    geofencesTree = 'geofencesTree',
}

export enum TreeDisplayStateType {
    mainObjectsTree = 'mainObjectsTree',
    mainGeofencesTree = 'mainGeofencesTree',
    dropDownGeofencesTree = 'dropDownGeofencesTree',
}

export interface FMTreeSearchData {
    lastText: string;
    foundNodes: SelectedTreeNode[];
    lastIndex: number;
}

export interface NodeInfoData {
    id: string;
    type: TreeNodeType;
    params: NodeParam[];
    showMinimizeSettings?: boolean;
}

export interface NodeEditData extends NodeInfoData {
    nodeType: TreeNodeType;
}

export interface GeofenceEditData extends NodeEditData {
    geofenceType: GeofenceType;
    points: any[];
    hasChanged: boolean;
}

export interface NodeParam {
    sysName: string;
    type: ParamType;
    editable?: boolean;
    label: string;
    value: any;
}

export interface NodeTextParam extends NodeParam {
    maxLength: number;
}

export interface NodeNumberParam extends NodeParam {
    decimalPrecision: number;
    min: number;
    max: number;
}

export enum TreeViewType {
    tree = 1,
    list = 2,
}

export interface IconData {
    id: string;
    name: string;
}

export interface OptimizeData {
    minDistanceBetweenPoints: number;
    allowedDeviationFromLine: number;
    optimizationComplete: boolean;
    currentPointsCount: number;
    newPointsCount: number;
    currentArea: number;
    newArea: number;
    areaDeviationValue: number;
    pointsCompressRate: number;
    points: GeoPoint[];
    isFetching: boolean;
}

export interface GeoPoint {
    num: number;
    lat: number;
    lon: number;
}

const initialTree: FMTreeData = {
    [TreeNodeType.item]: {
        byIds: {},
        allIds: [],
    },
    [TreeNodeType.group]: {
        byIds: {},
        allIds: [],
    },
    isFetching: false,
};

const initialSharedDisplayState: FMSharedDisplayState = {
    [TreeNodeType.item]: {
        checked: [],
    },
    [TreeNodeType.group]: {
        checked: [],
        expanded: [],
    },
    selectedNode: null,
    infoPanelFetching: false,
    infoPanelInEditMode: false,
    searchData: {
        lastText: '',
        foundNodes: [],
        lastIndex: -1,
    },
    nodeInfoData: null,
    nodeEditData: null,
    viewType: TreeViewType.tree,
    infoPanelInOptimizeMode: false,
    optimizeData: {
        minDistanceBetweenPoints: 35,
        allowedDeviationFromLine: 15,
        optimizationComplete: false,
        currentPointsCount: 0,
        newPointsCount: 0,
        currentArea: 0,
        newArea: 0,
        areaDeviationValue: 0,
        pointsCompressRate: 0,
        points: [],
        isFetching: false,
    },
};

const fmTreesDataInitialState: Record<string, FMTreeData> = {
    [TreeType.objectsTree]: initialTree,
    [TreeType.geofencesTree]: initialTree,
};

const fmTreesDisplayInitialState: Record<string, FMSharedDisplayState> = {
    [TreeDisplayStateType.mainGeofencesTree]: initialSharedDisplayState,
    [TreeDisplayStateType.dropDownGeofencesTree]: initialSharedDisplayState,
};

const fmTreesData = (state = fmTreesDataInitialState, action: Action): Record<string, FMTreeData> =>
    produce(state, (draftState) => {
        switch (action.type) {
            case constants.REPLACE_FETCHING: {
                const { treeType, isFetching } = action;
                draftState[treeType].isFetching = isFetching;
                break;
            }
            case constants.REPLACE_ITEMS_AND_GROUPS_DATA: {
                const { data, treeType } = action;
                draftState[treeType][TreeNodeType.group] = data[TreeNodeType.group];
                draftState[treeType][TreeNodeType.item] = data[TreeNodeType.item];
                draftState[treeType].isFetching = false;
                draftState[treeType][TreeNodeType.group].byIds['0'].name = '';
                break;
            }
            case constants.REPLACE_GROUPS_DATA: {
                const { treeType, byIds, allIds } = action;
                draftState[treeType][TreeNodeType.group].byIds = byIds;
                draftState[treeType][TreeNodeType.group].allIds = allIds;
                break;
            }
            case constants.REPLACE_ITEMS_DATA: {
                const { treeType, byIds, allIds } = action;
                draftState[treeType][TreeNodeType.item].byIds = byIds;
                draftState[treeType][TreeNodeType.item].allIds = allIds;
                break;
            }
            case constants.REPLACE_NODE_DATA: {
                const { treeType, nodeData, nodeType } = action;
                draftState[treeType][nodeType].byIds[nodeData.id] = nodeData;
                break;
            }
            case constants.REPLACE_CHILD_GROUPS_IDS: {
                const { treeType, groupId, childGroupsIds } = action;
                (draftState[treeType][TreeNodeType.group].byIds[
                    groupId
                ] as FMTreeGroupData).childGroups = childGroupsIds;
                break;
            }
            case constants.REPLACE_CHILD_ITEMS_IDS: {
                const { treeType, groupId, childItemsIds } = action;
                (draftState[treeType][TreeNodeType.group].byIds[groupId] as FMTreeGroupData).childItems = childItemsIds;
                break;
            }
            case constants.REPLACE_ALL_IDS: {
                const { treeType, treeNodeType, allIds } = action;
                draftState[treeType][treeNodeType].allIds = allIds;
                break;
            }
        }
    });

const fmTreesDisplay = (state = fmTreesDisplayInitialState, action: Action): Record<string, FMSharedDisplayState> =>
    produce(state, (draftState) => {
        switch (action.type) {
            case constants.REPLACE_INFO_PANEL_FETCHING: {
                const { treeDisplayState, isFetching } = action;
                draftState[treeDisplayState].infoPanelFetching = isFetching;
                break;
            }
            case constants.REPLACE_EXPANDED_NODES: {
                const { treeDisplayState, expanded, treeNodeType } = action;
                draftState[treeDisplayState][treeNodeType].expanded = expanded;
                break;
            }
            case constants.REPLACE_CHECKED_NODES: {
                const { treeDisplayState, checked, treeNodeType } = action;
                if (treeNodeType == TreeNodeType.item) {
                    window.localStorage.setItem('checkedGeozonesIds', JSON.stringify(checked));
                }
                draftState[treeDisplayState][treeNodeType].checked = checked;
                break;
            }
            case constants.REPLACE_SELECTED_NODE: {
                const { treeDisplayState, selectedNode } = action;
                draftState[treeDisplayState].selectedNode = selectedNode;
                draftState[treeDisplayState].infoPanelInOptimizeMode = false;
                draftState[treeDisplayState].optimizeData.optimizationComplete = false;
                break;
            }
            case constants.REPLACE_SEARCH_DATA: {
                const { treeDisplayState, searchData } = action;
                draftState[treeDisplayState].searchData = searchData;
                break;
            }
            case constants.REPLACE_INFO_PANEL_EDIT_MODE: {
                const { treeDisplayState, infoPanelInEditMode } = action;
                draftState[treeDisplayState].infoPanelInEditMode = infoPanelInEditMode;
                if (infoPanelInEditMode) {
                    draftState[treeDisplayState].infoPanelInOptimizeMode = false;
                    draftState[treeDisplayState].optimizeData.optimizationComplete = false;
                }
                break;
            }
            case constants.REPLACE_NODE_INFO_DATA: {
                const { treeDisplayState, nodeData } = action;
                draftState[treeDisplayState].nodeInfoData = nodeData;
                break;
            }
            case constants.REPLACE_NODE_EDIT_DATA: {
                const { treeDisplayState, nodeData } = action;
                draftState[treeDisplayState].nodeEditData = nodeData;
                break;
            }
            case constants.REPLACE_TREE_VIEW_TYPE: {
                const { treeDisplayState, treeViewType } = action;
                draftState[treeDisplayState].viewType = treeViewType;
                break;
            }
            case constants.REPLACE_INFO_PANEL_OPTIMIZE_MODE: {
                const { treeDisplayState, infoPanelInOptimizeMode } = action;
                draftState[treeDisplayState].infoPanelInOptimizeMode = infoPanelInOptimizeMode;
                if (!infoPanelInOptimizeMode) {
                    draftState[treeDisplayState].infoPanelInEditMode = false;
                    break;
                }
                draftState[treeDisplayState].infoPanelInEditMode = false;
                const optimizeData = draftState[treeDisplayState].optimizeData;
                draftState[treeDisplayState].optimizeData.optimizationComplete = false;
                optimizeData.minDistanceBetweenPoints = MIN_DISTANCE_BETWEEN_POINTS_DEFAULT_VALUE;
                optimizeData.allowedDeviationFromLine = ALLOWED_DEVIATION_FROM_LINE_DEFAULT_VALUE;
                optimizeData.isFetching = false;
                break;
            }
            case constants.REPLACE_OPTIMIZE_DATA: {
                const tree = draftState[TreeDisplayStateType.mainGeofencesTree];
                tree.optimizeData = action.payload;
                tree.infoPanelInEditMode = true;
                const editData: GeofenceEditData = {
                    geofenceType: GeofenceType.polygon,
                    points: action.payload.points,
                    nodeType: TreeNodeType.item,
                    id: 'optimizeEdit',
                    type: TreeNodeType.item,
                    params: [],
                    hasChanged: false,
                };
                tree.nodeEditData = editData;
                break;
            }
            case constants.REPLACE_OPTIMIZE_MIN_DISTANCE_BETWEEN_POINTS: {
                const optimizeData = draftState[TreeDisplayStateType.mainGeofencesTree].optimizeData;
                optimizeData.minDistanceBetweenPoints = action.payload;
                break;
            }
            case constants.REPLACE_OPTIMIZE_ALLOWED_DEVIATION_FROM_LINE: {
                const optimizeData = draftState[TreeDisplayStateType.mainGeofencesTree].optimizeData;
                optimizeData.allowedDeviationFromLine = action.payload;
                break;
            }
            case constants.CLOSE_OPTIMIZE_EDIT_MODE: {
                draftState[TreeDisplayStateType.mainGeofencesTree].infoPanelInEditMode = false;
                break;
            }
            case constants.REPLACE_EDIT_HAS_CHANGED: {
                const data = draftState[TreeDisplayStateType.mainGeofencesTree].nodeEditData as GeofenceEditData;
                data.hasChanged = action.payload;
                break;
            }
            case constants.REPLACE_NEW_AREA: {
                const state = draftState[TreeDisplayStateType.mainGeofencesTree];
                state.optimizeData.newArea = action.payload;
                (state.nodeEditData as GeofenceEditData).hasChanged = false;
                break;
            }
            case constants.REPLACE_OPTIMIZE_FETCHING: {
                draftState[TreeDisplayStateType.mainGeofencesTree].optimizeData.isFetching = action.payload;
                break;
            }
        }
    });

export interface State {
    fmTreesData: Record<string, FMTreeData>;
    fmTreesDisplay: Record<string, FMSharedDisplayState>;
}

export default combineReducers<State>({
    fmTreesData,
    fmTreesDisplay,
});
